/**
 * A CONFIGURAÇÃO DO PERCURSO: quais itens, em que ordem, com qual variante.
 *
 * A unidade da lista é o ITEM-MODELO, não a questão pronta. Por padrão cada entrada
 * gera uma variante nova a cada percurso — é o que faz o aluno aprender o
 * procedimento e não a resposta. Quando o professor quer uma questão específica (a
 * que ele viu e aprovou no laboratório), a entrada guarda a seed e a versão do item:
 * a mesma seed na mesma versão reproduz exatamente a mesma questão.
 *
 * Fica no `localStorage` deste navegador. Não é persistência de plataforma — é o
 * suficiente para um protótipo, e o formato versionado (`v: 1`) permite migrar depois.
 */

import { deBase64url, paraBase64url } from '../core/base64url.ts';
import { seed as comoSeed } from '../core/rng.ts';
import { generate } from '../engine/generate.ts';
import { getTemplate, hasTemplate, REGISTRY, type AnyTemplate } from '../engine/registry.ts';
import type { ItemInstance } from '../schema/item.ts';
import { DEMO } from './demo.ts';

export interface EntradaPercurso {
  /** Identidade da ENTRADA na lista — o mesmo item pode entrar mais de uma vez. */
  readonly id: string;
  readonly templateId: string;
  /** Variante fixa: sempre esta seed. Ausente: uma variante nova a cada percurso. */
  readonly seed?: string;
  /** Versão do item quando a seed foi fixada. */
  readonly versao?: number;
}

export type OrdemPercurso = 'lista' | 'dificuldade' | 'aleatoria';

export const ORDEM_TXT: Readonly<Record<OrdemPercurso, string>> = {
  lista: 'Na ordem da lista',
  dificuldade: 'Da mais fácil para a mais difícil',
  aleatoria: 'Em ordem aleatória',
};

export interface ConfigPercurso {
  readonly v: 1;
  readonly entradas: readonly EntradaPercurso[];
  readonly ordem: OrdemPercurso;
}

const CHAVE = 'itens-iterativos:percurso';

/** Disparado em `window` sempre que a configuração muda, de qualquer aba. */
export const EVENTO_MUDOU = 'itens-iterativos:percurso-mudou';

export function novoId(): string {
  return `e${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * O percurso de quem chega sem nada montado: as questões da demonstração, com as
 * variantes fixas (ver `demo.ts`).
 *
 * Antes eram os 35 itens do banco, e "Começar" gerava os 35 de uma vez — meia dúzia de
 * segundos de tela parada antes da primeira questão. Quem quiser o banco inteiro pede
 * por "Todos os itens", no montador.
 */
export function configPadrao(): ConfigPercurso {
  return { v: 1, ordem: 'lista', entradas: DEMO.map((e) => ({ id: novoId(), ...e })) };
}

/** Todos os itens do banco, uma vez cada, na ordem do banco (N0 → N4). */
export function configTodos(): ConfigPercurso {
  return {
    v: 1,
    ordem: 'lista',
    entradas: REGISTRY.map((t) => ({ id: novoId(), templateId: t.id as string })),
  };
}

export function carregarConfig(): ConfigPercurso {
  try {
    const bruto = localStorage.getItem(CHAVE);
    if (bruto) {
      const c = JSON.parse(bruto) as Partial<ConfigPercurso> | null;
      if (c && c.v === 1 && Array.isArray(c.entradas)) {
        const ordem: OrdemPercurso =
          c.ordem === 'dificuldade' || c.ordem === 'aleatoria' ? c.ordem : 'lista';
        // Uma entrada de item que saiu do banco é descartada em silêncio: não há o que
        // gerar a partir dela.
        const entradas = c.entradas.filter(
          (e) =>
            typeof e?.id === 'string' &&
            typeof e?.templateId === 'string' &&
            hasTemplate(e.templateId),
        );
        return { v: 1, ordem, entradas };
      }
    }
  } catch {
    // armazenamento bloqueado ou corrompido: cai no padrão
  }
  return configPadrao();
}

export function salvarConfig(c: ConfigPercurso): void {
  try {
    localStorage.setItem(CHAVE, JSON.stringify(c));
  } catch {
    // sem persistência: a mudança vale só para esta sessão
  }
  window.dispatchEvent(new CustomEvent(EVENTO_MUDOU));
}

// ---------------------------------------------------------------------------
// Compartilhar
// ---------------------------------------------------------------------------

/**
 * O PERCURSO NUM CÓDIGO: `P1.` seguido do JSON compacto em base64url — só letras,
 * números, ponto, hífen e sublinhado, para caber num link ou numa mensagem sem ser
 * quebrado. Guarda os itens, a ordem e as variantes fixadas (seed e versão); os ids
 * das entradas não viajam, são refeitos na chegada.
 *
 * O prefixo com versão permite mudar o formato depois sem invalidar os códigos já
 * distribuídos.
 */
const PREFIXO_CODIGO = 'P1.';

export function codigoDoPercurso(c: ConfigPercurso): string {
  const o = c.ordem === 'dificuldade' ? 'd' : c.ordem === 'aleatoria' ? 'a' : 'l';
  const e = c.entradas.map((x) => (x.seed ? [x.templateId, x.seed, x.versao ?? 0] : x.templateId));
  return PREFIXO_CODIGO + paraBase64url(JSON.stringify({ o, e }));
}

/**
 * Lê um código de percurso — ou um link inteiro que termine com ele. Devolve `null`
 * para qualquer coisa que não seja um percurso com pelo menos um item do banco.
 */
export function percursoDoCodigo(texto: string): ConfigPercurso | null {
  const t = texto.trim().replace(/^.*#p=/, '');
  if (!t.startsWith(PREFIXO_CODIGO)) return null;
  try {
    const obj = JSON.parse(deBase64url(t.slice(PREFIXO_CODIGO.length))) as {
      o?: unknown;
      e?: unknown;
    } | null;
    if (!obj || !Array.isArray(obj.e)) return null;
    const ordem: OrdemPercurso =
      obj.o === 'd' ? 'dificuldade' : obj.o === 'a' ? 'aleatoria' : 'lista';
    const entradas: EntradaPercurso[] = [];
    for (const x of obj.e as unknown[]) {
      if (typeof x === 'string') {
        if (hasTemplate(x)) entradas.push({ id: novoId(), templateId: x });
      } else if (
        Array.isArray(x) &&
        typeof x[0] === 'string' &&
        typeof x[1] === 'string' &&
        hasTemplate(x[0])
      ) {
        const versao = typeof x[2] === 'number' && x[2] > 0 ? x[2] : undefined;
        entradas.push(
          versao === undefined
            ? { id: novoId(), templateId: x[0], seed: x[1] }
            : { id: novoId(), templateId: x[0], seed: x[1], versao },
        );
      }
    }
    return entradas.length > 0 ? { v: 1, ordem, entradas } : null;
  } catch {
    return null;
  }
}

/** O endereço desta página com o percurso no fim: `…#p=P1.…`. */
export function linkDoPercurso(c: ConfigPercurso): string {
  return `${location.href.split('#')[0]}#p=${codigoDoPercurso(c)}`;
}

/** Acrescenta um item ao fim do percurso — com variante fixa, se vier a seed. */
export function adicionarAoPercurso(templateId: string, seed?: string): void {
  const c = carregarConfig();
  const e: EntradaPercurso = seed
    ? { id: novoId(), templateId, seed, versao: getTemplate(templateId).version }
    : { id: novoId(), templateId };
  salvarConfig({ ...c, entradas: [...c.entradas, e] });
}

/**
 * A variante foi fixada numa versão anterior do item?
 *
 * A versão entra na semente: depois de uma correção no gerador, a mesma seed produz
 * OUTRA questão. Melhor dizer isso ao professor do que servir uma questão diferente
 * da que ele aprovou sem aviso.
 */
export function fixadaDesatualizada(e: EntradaPercurso): boolean {
  return (
    e.seed !== undefined &&
    e.versao !== undefined &&
    hasTemplate(e.templateId) &&
    getTemplate(e.templateId).version !== e.versao
  );
}

/** Sorteia seeds até uma ser aceita. Uma seed rejeitada nunca vira `seed + 1`. */
export function primeiraAceita(t: AnyTemplate): ItemInstance | null {
  for (let i = 0; i < 60; i++) {
    const r = generate(t, comoSeed(`p${Math.floor(Math.random() * 1_000_000)}`));
    if (r.status === 'ok') return r.instance;
  }
  return null;
}

export function instanciaDe(e: EntradaPercurso): ItemInstance | null {
  if (!hasTemplate(e.templateId)) return null;
  const t = getTemplate(e.templateId);
  if (e.seed) {
    const r = generate(t, comoSeed(e.seed));
    if (r.status === 'ok') return r.instance;
  }
  return primeiraAceita(t);
}

/** As questões do percurso, já geradas e na ordem configurada. */
export function montarInstancias(c: ConfigPercurso): ItemInstance[] {
  const out = c.entradas.map((e) => instanciaDe(e)).filter((x): x is ItemInstance => x !== null);
  if (c.ordem === 'dificuldade') {
    // Ordem por b̂ provisório: não é seleção adaptativa — essa precisa de dados que
    // ainda não existem —, mas é o máximo honesto que dá para fazer hoje.
    out.sort((a, b) => a.difficulty.provisionalB - b.difficulty.provisionalB);
  } else if (c.ordem === 'aleatoria') {
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const t = out[i] as ItemInstance;
      out[i] = out[j] as ItemInstance;
      out[j] = t;
    }
  }
  return out;
}
